import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Leaf, Sparkles, TreePine } from "lucide-react";
import EnvironmentMap from "@/components/environment/EnvironmentMap";
import EnvironmentMedia from "@/components/environment/EnvironmentMedia";
import EnvironmentProofPuzzle from "@/components/environment/EnvironmentProofPuzzle";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import ScrollReveal from "@/components/motion/ScrollReveal";
import HoverLift from "@/components/motion/HoverLift";
import { StaggerItem, StaggerReveal } from "@/components/motion/StaggerReveal";
import { ENVIRONMENT, ENVIRONMENT_HERO_IMAGE } from "@/data/environment";

export default function Environment() {
    return (
        <main
            data-testid="environment-page"
            className="min-h-screen bg-[#FAFAFA] text-zinc-950 overflow-x-hidden"
        >
            <Header />

            <section className="relative pt-24 sm:pt-28 md:pt-36 pb-10 sm:pb-12 md:pb-14 overflow-hidden text-white">
                <div
                    className="pointer-events-none absolute inset-0 bg-cover bg-no-repeat"
                    style={{
                        backgroundImage: `url(${ENVIRONMENT_HERO_IMAGE})`,
                        backgroundPosition: "center 28%",
                    }}
                    role="img"
                    aria-label="Community members preparing planting holes"
                />
                <div
                    className="pointer-events-none absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/80 to-zinc-950/40"
                    aria-hidden
                />
                <div
                    className="pointer-events-none absolute inset-0 bg-gradient-to-br from-emerald-950/30 via-zinc-950/25 to-[#1a0f0c]/40"
                    aria-hidden
                />
                <div
                    className="pointer-events-none absolute top-10 left-0 w-[280px] sm:w-[400px] h-[280px] sm:h-[400px] rounded-full bg-emerald-500/8 blur-[80px] sm:blur-[100px]"
                    aria-hidden
                />
                <div
                    className="pointer-events-none absolute top-20 right-0 w-[240px] sm:w-[360px] h-[240px] sm:h-[360px] rounded-full bg-[#E54D2E]/10 blur-[80px]"
                    aria-hidden
                />
                <div className="mx-auto max-w-[1400px] px-4 sm:px-6 md:px-10 lg:px-16 relative z-10">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-sm font-medium text-white/60 hover:text-white mb-6 sm:mb-10 transition-colors"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back to home
                    </Link>

                    <div className="max-w-3xl">
                        <div className="inline-flex flex-wrap items-center gap-2 mb-4 sm:mb-6">
                            <span className="inline-flex items-center gap-2 h-8 px-3 rounded-full bg-emerald-500/20 border border-emerald-400/30 text-xs font-medium text-emerald-100">
                                <Leaf className="h-3.5 w-3.5" />
                                Sustainable Environment
                            </span>
                            <span className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white/10 border border-white/15 text-xs font-medium text-white/90">
                                <TreePine className="h-3.5 w-3.5 text-emerald-400" />
                                Dryland restoration
                            </span>
                        </div>
                        <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-[-0.03em] leading-[0.95]">
                            {ENVIRONMENT.title}
                        </h1>
                        <p className="mt-4 sm:mt-6 text-base sm:text-lg md:text-xl text-white/75 leading-relaxed max-w-2xl">
                            {ENVIRONMENT.tagline}
                        </p>
                        <p className="mt-4 text-sm sm:text-base text-white/60 leading-relaxed max-w-2xl">
                            {ENVIRONMENT.intro}
                        </p>
                        <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-3">
                            <Link
                                to="/hackathon"
                                className="inline-flex w-full sm:w-auto items-center justify-center gap-2 h-12 sm:h-14 px-6 sm:px-8 rounded-full bg-emerald-600 text-white text-sm sm:text-base font-medium hover:bg-white hover:text-zinc-950 transition-colors"
                            >
                                Join pre-activity
                                <ArrowUpRight className="h-4 w-4 shrink-0" />
                            </Link>
                            <a
                                href="#proof"
                                className="inline-flex w-full sm:w-auto items-center justify-center gap-2 h-12 sm:h-14 px-6 sm:px-8 rounded-full border border-white/20 text-white text-sm sm:text-base font-medium hover:bg-white/10 transition-colors"
                            >
                                See the proof
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-14 sm:py-20 md:py-28 bg-white border-b border-black/[0.06]">
                <div className="mx-auto max-w-[1400px] px-4 sm:px-6 md:px-10 lg:px-16">
                    <ScrollReveal className="max-w-2xl mb-10 sm:mb-14">
                        <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-emerald-700 mb-5">
                            <span className="h-px w-8 bg-emerald-400" />
                            How it works
                        </div>
                        <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-zinc-950 leading-[0.95]">
                            Land + skills,{" "}
                            <span className="text-emerald-700">together</span>.
                        </h2>
                    </ScrollReveal>
                    <StaggerReveal className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {ENVIRONMENT.pillars.map((pillar) => (
                            <StaggerItem key={pillar.title}>
                                <HoverLift className="h-full">
                                    <div className="motion-card h-full rounded-3xl border border-black/[0.06] bg-[#FAFAFA] p-6 md:p-8">
                                        <h3 className="font-display text-xl font-bold text-zinc-950">
                                            {pillar.title}
                                        </h3>
                                        <p className="mt-3 text-sm text-zinc-600 leading-relaxed">
                                            {pillar.detail}
                                        </p>
                                    </div>
                                </HoverLift>
                            </StaggerItem>
                        ))}
                    </StaggerReveal>
                </div>
            </section>

            <section
                id="proof"
                data-testid="environment-proof"
                className="py-14 sm:py-20 md:py-28 bg-zinc-950 text-white"
            >
                <div className="mx-auto max-w-[1400px] px-4 sm:px-6 md:px-10 lg:px-16">
                    <ScrollReveal className="mb-10 sm:mb-14 max-w-2xl">
                        <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-emerald-400 mb-5">
                            <span className="h-px w-8 bg-emerald-500/50" />
                            Proof on the ground
                        </div>
                        <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-tight leading-[0.95]">
                            Receipts from the field.
                        </h2>
                        <p className="mt-4 text-white/65 leading-relaxed">
                            {ENVIRONMENT.videoIntro}
                        </p>
                    </ScrollReveal>

                    <EnvironmentMedia />

                    <ScrollReveal className="mb-8 max-w-2xl">
                        <p className="text-sm text-white/55 leading-relaxed">
                            Photo proof from the ground — seedballs, trees, and
                            volunteers across our partner sites.
                        </p>
                    </ScrollReveal>

                    <EnvironmentProofPuzzle />
                </div>
            </section>

            <section
                id="sites"
                className="py-14 sm:py-20 md:py-28 bg-zinc-900 text-white border-t border-white/10"
            >
                <div className="mx-auto max-w-[1400px] px-4 sm:px-6 md:px-10 lg:px-16">
                    <ScrollReveal className="max-w-2xl mb-10 sm:mb-14">
                        <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-emerald-400 mb-5">
                            <span className="h-px w-8 bg-emerald-500/50" />
                            Where we work
                        </div>
                        <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-tight leading-[0.95]">
                            Projects & probable sites.
                        </h2>
                        <p className="mt-4 text-white/65 leading-relaxed">
                            Active restoration at Ngongongeri Farm and partnered
                            work at Mogotio Summer Rose Quarry — dryland sites
                            we are targeting for seedballs and native cover.
                        </p>
                    </ScrollReveal>
                    <EnvironmentMap />
                </div>
            </section>

            <section className="py-14 sm:py-20 md:py-28">
                <div className="mx-auto max-w-[1400px] px-4 sm:px-6 md:px-10 lg:px-16">
                    <div className="rounded-2xl sm:rounded-3xl bg-emerald-700 text-white p-8 sm:p-12 md:p-16 text-center">
                        <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-black tracking-tight">
                            Want to plant and build with us?
                        </h2>
                        <p className="mt-4 text-white/85 max-w-lg mx-auto leading-relaxed">
                            Join the next pre-activity or hackathon — restore
                            land in the morning, level up on AI in the
                            community.
                        </p>
                        <Link
                            to="/hackathon"
                            className="mt-8 inline-flex w-full sm:w-auto items-center justify-center gap-2 h-12 sm:h-14 px-8 rounded-full bg-white text-emerald-900 font-semibold hover:bg-zinc-950 hover:text-white transition-colors mx-auto max-w-sm sm:max-w-none"
                        >
                            Register on Luma
                            <Sparkles className="h-4 w-4" />
                        </Link>
                    </div>
                </div>
            </section>

            <Footer />
        </main>
    );
}
